/* eslint-disable no-underscore-dangle */
import React, {
  useEffect,
  FocusEvent,
  RefObject,
  ReactElement,
  ReactNode,
  useRef,
} from 'react';

import {
  SearchSuggestion,
  useKeyNavigation,
} from '@silvertours/front-entities';
import { Theme } from '@silvertours/front-shared';

import {
  DisplayExpressionWrapper,
  IconAirport,
  IconCity,
  IconRail,
  NoResultsItem,
  NoResultsItemWrapper,
  ResultItem,
  ResultsListWrapper,
} from './LocationSuggest.styles';

type LocationSuggestProps = {
  results: Array<SearchSuggestion> | null;
  connectedInputContainer?: RefObject<HTMLDivElement>;
  onItemClick: (value?: SearchSuggestion) => void;
  onLastElementBlur: (event: FocusEvent<HTMLElement, Element>) => void;
  noResultsText: string;
  loading: boolean;
};

const LocationSuggest = ({
  results,
  connectedInputContainer,
  onItemClick,
  onLastElementBlur,
  noResultsText,
  loading,
}: LocationSuggestProps) => {
  const listRef = useRef<HTMLUListElement>(null);

  const isMobileTouchDevice = Theme.useIsMobileTouchDevice();

  const handleEnter = (item?: SearchSuggestion) => {
    onItemClick(item);
  };

  const { focusedItem, onMouseEnter, onMouseLeave } =
    useKeyNavigation<SearchSuggestion>(results || [], listRef, handleEnter);

  useEffect(() => {
    if (isMobileTouchDevice && connectedInputContainer?.current) {
      connectedInputContainer.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isMobileTouchDevice, connectedInputContainer]);

  const getDisplayIconAndDescription = (
    suggestion: SearchSuggestion,
    suggest: boolean,
  ) => {
    let icon: ReactElement | null = null;
    let html: ReactNode | Element = '';

    switch (suggestion.type) {
      case 'city':
      case 'citywithoutrail':
        icon = <IconCity />;
        break;
      case 'multiairports':
      case 'multiairport_single':
      case 'airport':
        icon = <IconAirport />;
        break;
      case 'multirail_single':
      case 'multirail':
      case 'rail_only':
      case 'rail':
        icon = <IconRail />;
        break;
      case 'normal':
      case 'all':
      default:
        break;
    }

    if (!suggest) {
      icon = null;
    }

    html = suggestion.title;
    return {
      icon,
      html,
    } as Record<string, ReactNode | string>;
  };

  const getDisplayExpression = (
    suggestion: SearchSuggestion,
    suggest = true,
  ) => {
    const displayExpression = getDisplayIconAndDescription(suggestion, suggest);

    if (typeof displayExpression.html === 'string' && suggest) {
      displayExpression.html = (
        // eslint-disable-next-line react/no-danger
        <span dangerouslySetInnerHTML={{ __html: displayExpression.html }} />
      );
    }
    return (
      <>
        <DisplayExpressionWrapper>
          {displayExpression.icon}
        </DisplayExpressionWrapper>
        {displayExpression.html}
      </>
    );
  };

  function getNoResultsItem() {
    return loading ? null : (
      <NoResultsItemWrapper>
        <NoResultsItem>{noResultsText}</NoResultsItem>
      </NoResultsItemWrapper>
    );
  }

  return results && results.length ? (
    <ResultsListWrapper ref={listRef}>
      {results.map((suggestion: SearchSuggestion, index: number) => (
        <li
          // eslint-disable-next-line react/no-array-index-key
          key={suggestion.cityName + index}
        >
          <ResultItem
            type="button"
            tabIndex={0}
            onBlur={onLastElementBlur}
            onMouseDown={() => onItemClick(suggestion)}
            onMouseEnter={() => onMouseEnter(index)}
            onMouseLeave={onMouseLeave}
            selected={index === focusedItem}
          >
            {getDisplayExpression(suggestion)}
          </ResultItem>
        </li>
      ))}
    </ResultsListWrapper>
  ) : (
    getNoResultsItem()
  );
};

export { LocationSuggest };
